// =============================================================================
// components/notifications/InAppNotification.tsx — LR7 (bare-RN P3-8 port)
//
// ONE mounted-once host that owns the FOREGROUND notification experience:
//   1. In-app banner: when a `notification` socket event arrives while the app
//      is foregrounded, slide a tappable glass banner down from the top. Tapping
//      it routes via the same data.type/url contract as the push tap handler
//      (so foreground and background taps land identically).
//   2. App-icon badge sync: the iOS app-icon badge is the NOTIFICATION unread
//      count (the single source of truth for the push/notification badge). It is
//      seeded from GET /notifications/unread-count, updated live from the socket
//      `notificationUnreadCount` event, and reconciled on socket reconnect.
//
//      ⚠️ BADGE OWNERSHIP (LR7): the app-icon badge counts NOTIFICATIONS ONLY.
//      LR2's DM unread is a SEPARATE in-nav dot on the Chat tab — it must NOT be
//      added to the app-icon badge here (no double-count). One number, one source.
//
// Degrades silently and NEVER throws — push/banner is non-critical.
// Mount once, high in the tree (App.tsx, inside the auth shell).
//
// ── BARE-RN MIGRATION (blueprint §1) ─────────────────────────────────────────
//   • expo-router useRouter()            → navigationRef imperative navigate. This
//     component renders ABOVE the navigator (a sibling of NavigationContainer's
//     content), so it has no screen context — it must use navigationRef, not a
//     useNavigation() hook (blueprint §1 row 1 NB).
//   • @expo/vector-icons Ionicons        → components/ui/Icon (Ionicons shim).
//   • expo-notifications setBadgeCount    → NATIVE app-icon badge is P4 (notifee/
//     FCM/APNs). For now setAppBadge() is a graceful no-op: the SOCKET-driven
//     in-app banner + the badge BOOKKEEPING (seed/live/reconcile) stay fully wired
//     so P4 only swaps the one setter. No native push dependency is added here.
//
// The relative in-app route strings ('/post/123', '/user/jane', …) are mapped to
// react-navigation routes by routeToNavTarget() below (the §2 contract).
// =============================================================================
import { useEffect, useRef, useState, useCallback } from 'react';
import { Animated, StyleSheet, Text, TouchableOpacity, View, Platform, Linking } from 'react-native';
import CachedImage from '../ui/CachedImage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '../ui/Icon';
import { navigate } from '../../navigation/navigationRef';
import {
  onNotification,
  onNotificationUnreadCount,
  onReconnect,
  fetchUnreadCounts,
} from '../../services/socket';
import { api, getToken } from '../../services/api';
import { colors, spacing, radius } from '../../constants/theme';

type Banner = {
  id: string;
  title: string;
  body: string;
  avatar?: string;
  route?: string | null;
};

// Resolve a socket notification payload to a relative in-app route. Mirrors the
// data.type/url contract used by the (P4) push-tap handler (kept in sync).
function routeForNotification(n: any): string | null {
  if (!n || typeof n !== 'object') return null;
  const data = n.data || n.metadata || n;
  const explicit = data?.url ?? data?.actionUrl ?? n?.actionUrl;
  if (typeof explicit === 'string' && explicit.startsWith('/') && !explicit.includes('..')) {
    return explicit;
  }
  const type = String(n.type || data?.type || '').toLowerCase();
  switch (type) {
    case 'dm':
    case 'message': {
      const id = data?.conversationId ?? data?.channelId ?? data?.senderId;
      return id ? `/dm/${id}` : '/chat';
    }
    case 'post_like':
    case 'like':
    case 'comment':
    case 'reply':
    case 'mention': {
      const id = data?.postId ?? data?.entityId ?? n?.postId;
      return id ? `/post/${id}` : '/notifications';
    }
    case 'follow':
    case 'friend_request': {
      const u = data?.username ?? data?.actorUsername ?? n?.actor?.username;
      return u ? `/user/${u}` : '/notifications';
    }
    case 'community':
    case 'community_post':
    case 'server_invite': {
      const c = data?.communityName ?? data?.communitySlug ?? data?.serverId;
      return c ? `/community/${c}` : '/communities';
    }
    default:
      return '/notifications';
  }
}

// Map a relative in-app route to a react-navigation navigate() call (blueprint §2
// route contract). Returns true if it navigated, false if the route was unknown.
function navigateToRoute(route: string): boolean {
  // Strip a leading slash + split into segments: '/post/123' -> ['post','123'].
  const parts = route.replace(/^\/+/, '').split('/').filter(Boolean);
  const [head, arg] = parts;
  switch (head) {
    case 'post':
      if (arg) { navigate('Post', { id: arg }); return true; }
      navigate('Notifications'); return true;
    case 'user':
      if (arg) { navigate('UserProfile', { username: arg }); return true; }
      navigate('Notifications'); return true;
    case 'community':
      if (arg) { navigate('Community', { name: arg }); return true; }
      navigate('Communities'); return true;
    case 'communities':
      navigate('Communities'); return true;
    case 'dm':
      if (arg) { navigate('DM', { id: arg }); return true; }
      navigate('Tabs', { screen: 'Chat' }); return true;
    case 'chat':
      navigate('Tabs', { screen: 'Chat' }); return true;
    case 'notifications':
      navigate('Notifications'); return true;
    default:
      return false;
  }
}

function titleFor(n: any): string {
  return (
    n?.title ||
    n?.actor?.displayName ||
    n?.actor?.username ||
    (n?.actorUsername ? `@${n.actorUsername}` : '') ||
    'CRYB'
  );
}

function bodyFor(n: any): string {
  return (n?.body || n?.message || n?.text || 'You have a new notification').toString();
}

// Best-effort set of the iOS app-icon badge to the live NOTIFICATION unread
// count. Never throws.
//
// Bare-RN has no custom native module, so JS signals native via an internal
// Linking URL (cryb://internal/badge?count=N); the AppDelegate sets the icon
// badge (UNUserNotificationCenter.setBadgeCount / applicationIconBadgeNumber).
// Background pushes ALSO carry `badge` so the icon stays correct while the app
// is closed; this setter keeps it live while foregrounded. We de-dupe so an
// unchanged count doesn't re-fire the URL.
let lastBadge = -1;
async function setAppBadge(count: number): Promise<void> {
  try {
    const c = Math.max(0, Math.floor(count || 0));
    if (c === lastBadge) return;
    lastBadge = c;
    if (Platform.OS === 'ios') {
      await Linking.openURL(`cryb://internal/badge?count=${c}`);
    }
  } catch {
    // best-effort: a background push's `badge` still keeps the icon correct.
  }
}

export default function InAppNotification() {
  const insets = useSafeAreaInsets();
  const [banner, setBanner] = useState<Banner | null>(null);
  const translateY = useRef(new Animated.Value(-200)).current;
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    Animated.timing(translateY, {
      toValue: -200,
      duration: 220,
      useNativeDriver: true,
    }).start(() => setBanner(null));
  }, [translateY]);

  const show = useCallback(
    (b: Banner) => {
      setBanner(b);
      Animated.spring(translateY, {
        toValue: 0,
        useNativeDriver: true,
        bounciness: 6,
        speed: 14,
      }).start();
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => dismiss(), 4200);
    },
    [translateY, dismiss],
  );

  // ── In-app banner: live notifications while foregrounded ──────────────────
  useEffect(() => {
    const unsub = onNotification((payload: any) => {
      try {
        if (!payload || typeof payload !== 'object') return;
        show({
          id: String(payload?.id || Date.now()),
          title: titleFor(payload),
          body: bodyFor(payload),
          avatar: payload?.actor?.avatar || payload?.avatar || undefined,
          route: routeForNotification(payload),
        });
      } catch {
        // ignore malformed payloads
      }
    });
    return unsub;
  }, [show]);

  // ── App-icon badge = notification unread count (single source) ────────────
  // Seed once from REST, then track the live socket count, and reconcile on
  // reconnect (counts can drift across a disconnect window).
  useEffect(() => {
    let active = true;

    // Seed from the canonical endpoint — but ONLY when authenticated. Firing this
    // authed poll while logged out (no token) is wasteful and used to trip the
    // no-token 401 path; skip cleanly when there's no session.
    (async () => {
      try {
        if (!(await getToken())) return;
        const res = await api<any>('/notifications/unread-count');
        if (!active) return;
        const r = res as any;
        const count = r?.count ?? r?.data?.count ?? r?.unreadCount ?? 0;
        await setAppBadge(count);
      } catch {
        // non-fatal
      }
    })();

    // Live updates.
    const unsubCount = onNotificationUnreadCount((count: number) => {
      setAppBadge(count);
    });

    // Reconnect reconcile (LR1 spine): pull fresh counts and re-apply.
    const unsubReconnect = onReconnect(async () => {
      try {
        const counts = await fetchUnreadCounts();
        if (counts && typeof counts.notifications === 'number') {
          await setAppBadge(counts.notifications);
        }
      } catch {
        // non-fatal
      }
    });

    return () => {
      active = false;
      unsubCount();
      unsubReconnect();
    };
  }, []);

  if (!banner) return null;

  const topPad = (Platform.OS === 'ios' ? insets.top : insets.top + spacing.sm) + spacing.xs;

  return (
    <Animated.View
      pointerEvents="box-none"
      style={[styles.host, { transform: [{ translateY }] }]}
    >
      <TouchableOpacity
        activeOpacity={0.9}
        onPress={() => {
          const route = banner.route;
          dismiss();
          if (route && route.startsWith('/')) {
            try { navigateToRoute(route); } catch {}
          }
        }}
        style={[styles.banner, { marginTop: topPad }]}
      >
        {banner.avatar ? (
          <CachedImage source={{ uri: banner.avatar }} style={styles.avatar} />
        ) : (
          <View style={styles.iconWrap}>
            <Ionicons name="notifications" size={18} color={colors.primary} />
          </View>
        )}
        <View style={styles.textWrap}>
          <Text style={styles.title} numberOfLines={1}>
            {banner.title}
          </Text>
          <Text style={styles.body} numberOfLines={2}>
            {banner.body}
          </Text>
        </View>
        <TouchableOpacity onPress={dismiss} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <Ionicons name="close" size={18} color={colors.muted} />
        </TouchableOpacity>
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  host: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    paddingHorizontal: spacing.lg,
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.96)',
    borderRadius: radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.cardBorder,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
    shadowColor: colors.primary,
    shadowOpacity: 0.18,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  avatar: { width: 36, height: 36, borderRadius: 18, backgroundColor: colors.bg },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(108,92,231,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textWrap: { flex: 1 },
  title: { color: colors.ink, fontWeight: '700', fontSize: 14 },
  body: { color: colors.ink2, fontSize: 13, marginTop: 1 },
});
